import { Box, Typography } from "@mui/material";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";
import AnimatedNumber from "./AnimatedNumber";

/**
 * Compact stat card content for when a chart card is in "bold_number" mode.
 * Shows the total, average, min/max, trend indicator, and a mini sparkline.
 *
 * Props:
 * - total: the main number
 * - dataValues: array of time-series values (for avg/min/max/sparkline)
 * - color: hex color for accent
 * - isCurrency: whether to format as currency
 * - section: AnimatedNumber section key
 */
const BoldNumberCard = ({
  total,
  dataValues = [],
  color = "#009efd",
  isCurrency = false,
  section = "chartTotals",
}) => {
  const nonZero = dataValues.filter((v) => v != null && v !== 0);
  const avg =
    nonZero.length > 0
      ? nonZero.reduce((s, v) => s + v, 0) / nonZero.length
      : 0;
  const min = nonZero.length > 0 ? Math.min(...nonZero) : 0;
  const max = nonZero.length > 0 ? Math.max(...nonZero) : 0;

  // Trend: compare last period to average
  const lastVal = nonZero.length > 0 ? nonZero[nonZero.length - 1] : 0;
  const trendUp = lastVal > avg;
  const trendDown = lastVal < avg * 0.9;

  // Sparkline bars (last 8 values)
  const spark = dataValues.slice(-8);
  const sparkMax = Math.max(...spark.map((v) => Math.abs(v || 0)), 1);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Big number */}
      <Typography
        variant="h3"
        sx={{ fontWeight: "bold", fontSize: "2.5rem", color: "text.primary" }}
      >
        <AnimatedNumber
          section={section}
          value={total}
          isCurrency={isCurrency}
        />
      </Typography>

      {/* Mini sparkline */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: "3px",
          height: 32,
        }}
      >
        {spark.map((val, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: `${Math.max((Math.abs(val || 0) / sparkMax) * 100, 4)}%`,
              borderRadius: "2px",
              backgroundColor: color,
              opacity: i === spark.length - 1 ? 1 : 0.4,
              transition: "height 0.3s ease",
            }}
          />
        ))}
      </Box>

      {/* Sub-stats row */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            Avg
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            <AnimatedNumber
              section={section}
              value={Math.round(avg * 100) / 100}
              isCurrency={isCurrency}
            />
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            Min
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            <AnimatedNumber
              section={section}
              value={Math.round(min * 100) / 100}
              isCurrency={isCurrency}
            />
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            Max
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            <AnimatedNumber
              section={section}
              value={Math.round(max * 100) / 100}
              isCurrency={isCurrency}
            />
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            Trend
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {trendUp && <TrendingUp size={14} color="#22c55e" />}
            {trendDown && <TrendingDown size={14} color="#ef4444" />}
            {!trendUp && !trendDown && <Minus size={14} color="#6b7280" />}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: trendUp
                  ? "#22c55e"
                  : trendDown
                    ? "#ef4444"
                    : "text.secondary",
              }}
            >
              {trendUp ? "Up" : trendDown ? "Down" : "Stable"}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BoldNumberCard;
